// src/components/chat/AuthAttachment.jsx
// Renderiza un adjunto del chat usando el endpoint autenticado del backend
// (GET /chats/:chatId/messages/:msgId/attachments/:idx). Para imágenes/PDF
// crea un blob URL local; para "descargar" hace fetch + Blob + click sintético.
//
// Por qué este componente: el endpoint exige header Authorization, así que un
// <a href="..."> directo no sirve (el navegador no envía el header en target=_blank).
// La URL pública del archivo (storage local /uploads/chat/<random>) sigue
// existiendo pero queda como fallback no-recomendado para producción.
import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { Spinner } from 'react-bootstrap';

function isImage(mime) { return /^image\//.test(mime || ''); }
function isPdf(mime, name) {
    return mime === 'application/pdf' || /\.pdf$/i.test(name || '');
}

/** Hook: descarga un adjunto vía endpoint autenticado y devuelve un blob URL.
 *  Se revoca automáticamente al desmontar / cambiar de identificadores. */
export function useAuthBlobUrl(chatId, msgId, idx, enabled = true) {
    const [url, setUrl]         = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);

    useEffect(() => {
        if (!enabled || !chatId || !msgId || idx == null) return undefined;
        let cancelled = false;
        let blobUrl = null;
        setLoading(true);
        setError(null);

        api.get(`/chats/${chatId}/messages/${msgId}/attachments/${idx}`, { responseType: 'blob' })
            .then(res => {
                if (cancelled) return;
                blobUrl = URL.createObjectURL(res.data);
                setUrl(blobUrl);
            })
            .catch(err => {
                if (!cancelled) setError(err);
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => {
            cancelled = true;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [chatId, msgId, idx, enabled]);

    return { url, loading, error };
}

/** Click handler que descarga un adjunto autenticado y lo guarda con su nombre. */
export async function downloadAttachment(chatId, msgId, idx, filename) {
    const res = await api.get(`/chats/${chatId}/messages/${msgId}/attachments/${idx}`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'adjunto';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
}

/** Componente de adjunto. Decide tipo de render por mimeType.
 *  - imagen → thumb que abre la imagen full en nueva pestaña (blob URL)
 *  - PDF    → link + iframe inline
 *  - otro   → link "descargar"
 */
export default function AuthAttachment({ chatId, msgId, idx, att, ownColor }) {
    const wantsInline = isImage(att.mimeType) || isPdf(att.mimeType, att.name);
    const { url, loading } = useAuthBlobUrl(chatId, msgId, idx, wantsInline);
    const linkColor = ownColor ? '#fff' : '#2563eb';
    const openedRef = useRef(null);

    const openInNewTab = (e) => {
        e?.preventDefault?.();
        // Para imagen/PDF reutilizamos el blob URL ya descargado.
        if (url) {
            const w = window.open(url, '_blank', 'noopener,noreferrer');
            openedRef.current = w;
            return;
        }
        // Fallback: descarga directa.
        downloadAttachment(chatId, msgId, idx, att.name).catch(() => {});
    };

    if (isImage(att.mimeType)) {
        return (
            <div style={{ marginTop: 6 }}>
                {loading || !url ? (
                    <div style={{ width: 160, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.05)', borderRadius: 8 }}>
                        <Spinner size="sm" animation="border" />
                    </div>
                ) : (
                    <a href={url} onClick={openInNewTab} target="_blank" rel="noreferrer">
                        <img src={url} alt={att.name} style={{ maxWidth: 240, maxHeight: 180, borderRadius: 8, display: 'block' }} />
                    </a>
                )}
            </div>
        );
    }

    if (isPdf(att.mimeType, att.name)) {
        return (
            <div style={{ marginTop: 6 }}>
                <a
                    href={url || '#'}
                    onClick={openInNewTab}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: linkColor, fontSize: 13, textDecoration: 'underline', display: 'inline-block', marginBottom: 4 }}
                >
                    📄 {att.name}
                </a>
                {loading || !url ? (
                    <div style={{ width: 280, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.05)', borderRadius: 6 }}>
                        <Spinner size="sm" animation="border" />
                    </div>
                ) : (
                    <iframe
                        title={att.name}
                        src={url}
                        style={{ width: 280, height: 200, border: '1px solid rgba(0,0,0,.1)', borderRadius: 6, background: '#fff' }}
                    />
                )}
            </div>
        );
    }

    // Tipo arbitrario: link de descarga autenticada.
    return (
        <div style={{ marginTop: 6 }}>
            <button
                type="button"
                onClick={() => downloadAttachment(chatId, msgId, idx, att.name)}
                style={{ background: 'none', border: 'none', padding: 0, color: linkColor, fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}
            >
                📎 {att.name}
            </button>
        </div>
    );
}
